// SeatSelection.jsx – V2: Only seat selection here. Baggage/meal are selected on the fare card.
import { FaSpinner } from "react-icons/fa";

const SeatSelection = ({
  direction,
  showSeatSelection,
  availableSeats,
  selectedSeats,
  setSelectedSeats,
  isLoadingSeats,
  fetchSeats,
  selectedFlight,
  formData,
}) => {
  if (!showSeatSelection) return null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <h3 className="text-lg font-semibold mb-2">
        {direction} Seat Selection
      </h3>

      <button
        onClick={() =>
          fetchSeats(
            selectedFlight?.flightID || "",
            selectedFlight?.refID || "",
            direction
          )
        }
        disabled={isLoadingSeats}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
      >
        {isLoadingSeats ? (
          <span className="flex items-center gap-2">
            <FaSpinner className="animate-spin" /> Loading Seats...
          </span>
        ) : (
          "Load Available Seats"
        )}
      </button>

      {availableSeats.length > 0 ? (
        <SeatMap
          availableSeats={availableSeats}
          selectedSeats={selectedSeats}
          setSelectedSeats={setSelectedSeats}
          formData={formData}
        />
      ) : (
        !isLoadingSeats && (
          <div className="text-center py-4 text-gray-500">
            No seats loaded. Click "Load Available Seats" above.
          </div>
        )
      )}
    </div>
  );
};

export default SeatSelection;

/* -----------------------------------------------------------
    SEAT MAP SECTION (UNCHANGED FROM YOUR ORIGINAL LOGIC)
------------------------------------------------------------ */

const SeatMap = ({
  availableSeats,
  selectedSeats,
  setSelectedSeats,
  formData,
}) => {
  const maxRow = Math.max(...availableSeats.map((seat) => seat.row));
  const maxCol = Math.max(...availableSeats.map((seat) => seat.col));

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">Seat Map</h4>

      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="text-center mb-4">
          <div className="text-xs text-gray-500 mb-2">Aircraft Layout</div>

          <div className="flex justify-center items-center gap-2 text-xs">
            <Legend color="green" label="Available" />
            <Legend color="red" label="Booked" />
            <Legend color="blue" label="Selected" />
          </div>
        </div>

        {Array.from({ length: maxRow }, (_, row) => {
          const rowSeats = availableSeats.filter(
            (seat) => seat.row === row + 1
          );

          return (
            <div key={row} className="flex justify-center gap-1 mb-1">
              <div className="w-6 text-xs text-gray-500 flex justify-center">
                {row + 1}
              </div>

              {Array.from({ length: maxCol }, (_, col) => {
                const seat = rowSeats.find((s) => s.col === col + 1);

                if (!seat) return <div key={col} className="w-8 h-8" />;

                const isSelected = Object.values(selectedSeats).some(
                  (s) => s.seatID === seat.seatID
                );

                return (
                  <SeatButton
                    key={col}
                    seat={seat}
                    isSelected={isSelected}
                    setSelectedSeats={setSelectedSeats}
                    formData={formData}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {Object.keys(selectedSeats).length > 0 && (
        <SelectedSeatsSummary selectedSeats={selectedSeats} />
      )}
    </div>
  );
};

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-1">
    <div
      className={`w-3 h-3 border rounded ${
        color === "green"
          ? "bg-green-100 border-green-300"
          : color === "blue"
          ? "bg-blue-100 border-blue-300"
          : "bg-red-100 border-red-300"
      }`}
    ></div>
    <span>{label}</span>
  </div>
);

const SeatButton = ({ seat, isSelected, setSelectedSeats, formData }) => {
  const handleClick = () => {
    if (seat.isBooked) return;

    setSelectedSeats((prev) => {
      const newSeats = { ...prev };

      const maxSeats =
        (formData?.adults || 0) +
        (formData?.children || 0) +
        (formData?.infants || 0);

      if (isSelected) {
        delete newSeats[seat.seatID];
        return newSeats;
      }

      if (Object.keys(newSeats).length >= maxSeats) {
        const firstKey = Object.keys(newSeats)[0];
        delete newSeats[firstKey];
      }

      newSeats[seat.seatID] = seat;
      return newSeats;
    });
  };

  return (
    <button
      onClick={handleClick}
      disabled={seat.isBooked}
      className={`w-8 h-8 text-xs border rounded transition-colors ${
        seat.isBooked
          ? "bg-red-100 border-red-300 text-red-500 cursor-not-allowed"
          : isSelected
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-green-100 border-green-300 text-green-700 hover:bg-green-200"
      }`}
      title={`${seat.seatName} - ${
        seat.isBooked ? "Booked" : `₹${seat.seatAmt}`
      }`}
    >
      {seat.seatName}
    </button>
  );
};

const SelectedSeatsSummary = ({ selectedSeats }) => (
  <>
    <h5 className="text-sm font-medium mb-1">Selected Seats</h5>
    <div className="flex flex-wrap gap-2">
      {Object.values(selectedSeats).map((seat) => (
        <span
          key={seat.seatID}
          className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
        >
          {seat.seatName} (₹{seat.seatAmt})
        </span>
      ))}
    </div>

    <div className="mt-2 text-sm font-medium">
      Total Seat Cost: ₹
      {Object.values(selectedSeats).reduce(
        (sum, s) => sum + parseInt(s.seatAmt || 0),
        0
      )}
    </div>
  </>
);

